import { useTranslation } from 'react-i18next'
import { useState, useEffect } from 'react'
import { useOutletContext } from "react-router-dom"
import StateData from 'components/reporting/components/StateData/StateData'
import Bloc from 'components/reporting/components/Bloc/Bloc'
import DoughnutChart from 'components/reporting/components/graphs/Doughnut/Doughnut'
import HistogramChart from 'components/reporting/components/graphs/Histogram/Histogram'
import { getDatasFrom } from 'helpers/chartjs'
import colors from 'components/reporting/styles/colors.scss'

const ClaimsReport = () => {
  const { t } = useTranslation()
  const [contract] = useOutletContext()
  const [totalFiled, setTotalFiled] = useState(null)
  const [totalApproved, setTotalApproved] = useState(null)
  const [filedThisMonth, setFiledThisMonth] = useState(null)
  const [ratio, setRatio] = useState(null)
  const [processedClaims, setProcessedClaims] = useState(null)
  const [claimsPerPackage, setClaimsPerPackage] = useState(null)
  const [topClaims, setTopClaims] = useState(null)
  const [claimsStatus, setClaimsStatus] = useState(null)
  const [displayGraphs, setDisplayGraphs] = useState(false)


  useEffect(() => {
    if (!contract?._id) {
      setDisplayGraphs(false)
      return
    }

    const controller = new AbortController()
    const { signal } = controller

    // Reset datas
    setDisplayGraphs(true)
    setTotalFiled(null)
    setTotalApproved(null)
    setFiledThisMonth(null)
    setRatio(null)
    setProcessedClaims(null)
    setClaimsPerPackage(null)
    setTopClaims(null)
    setClaimsStatus(null)

    getDatasFrom({ contract, signal, endpoint: 'totalFiled' }).then(setTotalFiled)
    getDatasFrom({ contract, signal, endpoint: 'totalApproved' }).then(setTotalApproved)
    getDatasFrom({ contract, signal, endpoint: 'filedThisMonth' }).then(setFiledThisMonth)
    getDatasFrom({ contract, signal, endpoint: 'ratio' }).then(setRatio)
    getDatasFrom({ contract, signal, endpoint: 'processedClaims' }).then(setProcessedClaims)
    getDatasFrom({ contract, signal, endpoint: 'claimsPerPackage' }).then(setClaimsPerPackage)
    getDatasFrom({ contract, signal, endpoint: 'topClaims' }).then(setTopClaims)
    getDatasFrom({ contract, signal, endpoint: 'claimsStatus' }).then(setClaimsStatus)

    return () => {
      controller.abort()
    }
  }, [contract])

  return displayGraphs 
  ? (
    <>
      <StateData
        title={t('reporting:graphs.totalFiled')}
        data={totalFiled}
        size='quart'
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <StateData
        title={t('reporting:graphs.totalApproved')}
        data={totalApproved}
        size='quart'
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <StateData
        title={t('reporting:graphs.filedThisMonth')}
        data={filedThisMonth}
        size='quart'
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <StateData
        title={t('reporting:graphs.ratio')}
        data={ratio}
        size='quart'
        footerTitle={t('reporting:graphs.pastMonth')}
      />

      <Bloc
        title={t('reporting:graphs.processedClaims.title')}
        subtitle={t('reporting:graphs.range', {type: null})}
        content={<HistogramChart
          backgroundColor={[
            colors.blue1,
            colors.blue4,
            colors.orange2,
            colors.orange4
          ]}
          legendID='processedClaims'
          datas={processedClaims}
          monthsAxisX={true}
        />}
      />

      <Bloc
        title={t('reporting:graphs.claimsPerPackage.title')}
        subtitle={t('reporting:graphs.claimsPerPackage.subtitle', {number: totalFiled?.main ?? 0})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.blue1,
            colors.blue2,
            colors.blue3,
            colors.blue4,
            colors.blue5
          ]}
          legendID='claimsPerPackage'
          datas={claimsPerPackage}
        />}
      />

      <Bloc
        title={t('reporting:graphs.topClaims.title')}
        subtitle={t('reporting:graphs.topClaims.subtitle')}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.grey1,
            colors.grey2,
            colors.grey3,
            colors.grey4,
            colors.grey5
          ]}
          legendID='legendTopClaims'
          datas={topClaims}
        />}
      />
      
      <Bloc
        title={t('reporting:graphs.claimsStatus.title')}
        subtitle={t('reporting:graphs.claimsStatus.subtitle', {number: totalFiled?.main ?? 0})}
        size='tier'
        content={<DoughnutChart
          backgroundColor={[
            colors.grey1,
            colors.grey2,
            colors.grey3,
            colors.grey4,
            colors.grey5
          ]}
          legendID='claimsStatus'
          datas={claimsStatus}
        />}
      />
    </>
  )
  : t('reporting:noContract')
};

export default ClaimsReport
